import { ActivityDetailPdfVm, ActivityDetailVm } from '../../../../core/models/activity';
import { firstValueFrom } from 'rxjs';
import autoTable, { ColumnInput, RowInput } from 'jspdf-autotable';
import { ColumnKey, ColumnMappingService } from '../../../../core/services/column-mapping.service';
import moment from 'moment/moment';
import { TranslateService } from '@ngx-translate/core';
import { jsPDF } from 'jspdf';
import { CurrentUser } from '@/core/models/user';
import { ReportSettings } from '@/core/models/report-settings.interface';
import { Layer } from '@/core/models/layer';
import { DateRange } from '@/core/models/date-range';
import { ImageUtilsService } from '@/core/services/utils/image-utils.service';

export class PdfExporter {
    private isPro: boolean;
    private isAdminOrManager: boolean;

    public constructor(
        private readonly translate: TranslateService,
        private readonly columnMappingService: ColumnMappingService,
        private readonly imageUtilsService: ImageUtilsService,
        private readonly layers: Layer[],
        private readonly reportSettings: ReportSettings,
        currentUser: CurrentUser,
    ) {
        this.isPro = currentUser.isPro();
        this.isAdminOrManager = currentUser.isAdminOrManager();
    }

    public async export(
        totalDuration: string,
        dateRange: DateRange,
        activities: ActivityDetailVm[],
        logoUrl: string,
        filename: string,
    ): Promise<void> {
        const translations = await firstValueFrom(
            this.translate.get([
                'activities_user',
                'activities_note',
                'activities_date',
                'activities_time',
                'activities_duration',
                'activities_billed',
                'standard_yes',
                'standard_no',
                'activities_report',
                'activities_page',
                'pdf_words_to',
            ]),
        );

        const cols: ColumnInput[] = [];
        const addColumn = (dataKey: string, columnKey: ColumnKey): void => {
            if (
                this.columnMappingService.isColumnVisible(
                    columnKey,
                    this.isPro,
                    this.isAdminOrManager,
                    this.reportSettings,
                    this.layers,
                )
            ) {
                const header = this.columnMappingService.getColumnNameByKey(columnKey);
                cols.push({ dataKey, header });
            }
        };
        addColumn('userName', 'activities_user');
        addColumn('layer1', 'layer_1');
        addColumn('layer2', 'layer_2');
        addColumn('layer3', 'layer_3');
        addColumn('note', 'activities_note');
        addColumn('date', 'activities_date');
        addColumn('time', 'activities_time');
        addColumn('duration', 'activities_duration');
        addColumn('billed', 'activities_billed');

        const orientation = this.reportSettings.pdfOrientation;

        const doc = new jsPDF({
            orientation: orientation,
            unit: 'mm',
            format: orientation === 'portrait' ? 'a4' : [297, 210], // Use A4 for portrait, custom dimensions for landscape
        });

        const img = new Image();
        img.src = logoUrl;
        await img.decode();

        let pageNumber = 1;
        // Logo setup...
        const pageWidthMM = orientation === 'portrait' ? 210 : 297; // Adjust page width based on orientation
        const margins = { top: 5, left: 10, right: 14, bottom: 16 };
        const calculatedContentHeight = 22;
        const maxImageWidth = pageWidthMM * 0.25; // Adjust max image width based on page width
        const scaleWidth = maxImageWidth / img.width;
        const scaleHeight = calculatedContentHeight / img.height;
        const finalScale = Math.min(scaleWidth, scaleHeight);
        const finalWidth = img.width * finalScale;
        const finalHeight = img.height * finalScale;

        let imageXPosition: number;
        const imageYPosition: number = margins.top;

        if (orientation === 'portrait') {
            // For portrait orientation, adjust the X position to keep the logo on the right
            imageXPosition = pageWidthMM - margins.right - finalWidth; // Adjust X position for portrait
        } else {
            // Keep the original landscape setup
            imageXPosition = pageWidthMM - margins.right - finalWidth;
        }

        let imageData: string | HTMLImageElement = img;

        const imageType = this.imageUtilsService.getImageTypeForPdf(img.src);
        if (imageType === 'PNG') {
            try {
                imageData = await this.imageUtilsService.convertPngToJpegForPrint(img, finalWidth, finalHeight);
            } catch (e) {
                console.error('Error converting PNG to JPEG. Using the original PNG image for PDF generation...', e);
            }
        }

        doc.addImage(imageData, imageType, imageXPosition, imageYPosition, finalWidth, finalHeight);
        // Drawing the image within the calculated dimensions and position
        doc.setFontSize(5);

        const activityDetailPdfVms: ActivityDetailPdfVm[] = activities.map((a: ActivityDetailVm) => {
            const pdfActivity: ActivityDetailPdfVm = {
                userName: a.userName,
                layer1: a.layer1,
                layer2: a.layer2,
                layer3: a.layer3,
                note: a.note,
                date: a.date,
                time: a.time,
                duration: a.duration,
                billed: a.billed ? translations['standard_yes'] : translations['standard_no'],
            };
            return pdfActivity;
        });

        autoTable(doc, {
            columns: cols,
            headStyles: {
                fillColor: [234, 236, 239], // #EAECEF
                textColor: [73, 80, 87], // #495057
                fontStyle: 'bold',
            },
            bodyStyles: {
                textColor: [73, 80, 87],
            },
            margin: { top: 30 },
            body: activityDetailPdfVms as unknown as RowInput[], // TODO make sure the type is really correct...
            willDrawCell: (data) => {
                // Only apply this logic to 'body' cells for the background color
                if (data.section === 'body') {
                    const fillColor = data.row.index % 2 === 0 ? [248, 249, 250] : [255, 255, 255]; // Alternating between light grey and white
                    doc.setFillColor(fillColor[0], fillColor[1], fillColor[2]);
                    // Fill the cell with the background color before it is drawn
                    doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
                }
            },
            didDrawPage: (dataArg) => {
                // Bold and black report heading
                doc.setFontSize(20); // Set the heading font size
                doc.setFont('helvetica', 'bold'); // Make it bold
                doc.setTextColor(0, 0, 0); // Set color to black
                doc.text(this.reportSettings.heading, dataArg.settings.margin.left, 10);
                // Smaller, dark grey date and total time
                doc.setFontSize(10); // Smaller font size for date and total time
                doc.setFont('helvetica', 'normal'); // Normal font style for date
                doc.setTextColor(128, 128, 128); // Set color to dark grey
                // converts the dates based on the date format in report settings
                const startDate = moment(dateRange[0]);
                const endDate = moment(dateRange[1]);
                const startDateFormatted = startDate.format(this.reportSettings.dateFormat);
                const endDateFormatted = endDate.format(this.reportSettings.dateFormat);
                doc.text(
                    `${startDateFormatted} ${translations['pdf_words_to']} ${endDateFormatted}`,
                    dataArg.settings.margin.left,
                    17,
                );
                // Set text for "Total:" label in normal style
                doc.setFontSize(10); // Set font size for "Total:" label
                doc.setFont('helvetica', 'normal'); // Normal font style for "Total:" label
                doc.setTextColor(128, 128, 128); // Dark grey color
                doc.text('Total:', dataArg.settings.margin.left, 24);

                // Calculate the width of the "Total:" label to position the duration correctly
                const totalLabelWidth = doc.getTextWidth('Total: ');

                // Set the total duration value in bold and larger font size
                doc.setFontSize(12); // Larger font size for the duration value
                doc.setFont('helvetica', 'bold'); // Bold for the duration value
                doc.setTextColor(50, 50, 50); // Set color to black
                doc.text(totalDuration, dataArg.settings.margin.left + totalLabelWidth, 24);
                // Reset font style for the remaining text
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal'); // Reset to normal font style
                doc.setTextColor(0, 0, 0); // Reset text color to black for remaining text

                doc.setTextColor(0, 0, 0);
                doc.text(
                    `${translations['activities_page']} ${pageNumber}`,
                    dataArg.settings.margin.left + 1,
                    doc.internal.pageSize.getHeight() - 6,
                ); // translations.activities_page
                pageNumber++;
            },
        });
        doc.save(filename);
    }
}
